import { Link } from "@tanstack/react-router";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type {
	CartItemResponse,
	MenuItemResponse,
	MenuSizeResponse,
} from "@/api/lib/db/types";
import { CATEGORY_EXTRAS_MAP } from "@/src/lib/constants";
import Icon from "../../../lib/components/custom/Icon";
import { Image } from "../../../lib/components/custom/Image";
import { Badge } from "../../../lib/components/ui/badge";
import { Button } from "../../../lib/components/ui/button";
import { Dialog, DialogContent } from "../../../lib/components/ui/dialog";
import { useApi } from "../../../lib/hooks/api/use-api";
import { useSplashStore } from "../../../lib/hooks/use-store";
import ExtrasDialog from "./ExtrasDialog";

interface ItemDialogProps {
	item: MenuItemResponse | null;
	sizes?: MenuSizeResponse[];
	open: boolean;
	onOpenChange: (open: boolean) => void;
	cartItem?: CartItemResponse;
}

export default function ItemDialog({
	item,
	sizes = [],
	open,
	onOpenChange,
	cartItem,
}: ItemDialogProps) {
	const { t } = useTranslation();
	const [selectedSize, setSelectedSize] = useState<string>("");
	const [quantity, setQuantity] = useState(1);
	const [isAdding, setIsAdding] = useState(false);
	const [isExtrasOpen, setIsExtrasOpen] = useState(false);
	const [showVerifyBanner, setShowVerifyBanner] = useState(false);

	const { mutateAsync: addToCart } = useApi().cart.addItem;
	const { mutateAsync: updateItem } = useApi().cart.updateItem;
	const { data: session } = useApi().auth.useSession();
	const splash = useSplashStore();

	// Use the provided sizes (already filtered for this menu item)
	const itemSizes = sizes;

	// Reset state when dialog opens/closes
	React.useEffect(() => {
		if (open && item) {
			if (cartItem && itemSizes.length > 0) {
				// Edit mode
				if (cartItem.menuSizeId) {
					setSelectedSize(cartItem.menuSizeId.toString());
				}
				setQuantity(cartItem.quantity);
				setIsExtrasOpen(false);
			} else if (itemSizes.length > 0) {
				// Add mode
				setSelectedSize(itemSizes[0].id.toString());
				setQuantity(1);
				setIsExtrasOpen(false);
			}
		} else if (!open) {
			setSelectedSize("");
			setQuantity(1);
			setIsExtrasOpen(false);
			setShowVerifyBanner(false);
		}
	}, [open, item, itemSizes, cartItem]);

	const selectedSizeData = itemSizes.find(
		(size) => size.id.toString() === selectedSize,
	);
	const price = selectedSizeData ? parseFloat(selectedSizeData.price) : 0;
	const totalPrice = price * quantity;

	// Check if this item has available extras
	const hasExtras = useMemo(() => {
		if (!item) return false;
		const extrasCategories = CATEGORY_EXTRAS_MAP[item.category];
		return extrasCategories && extrasCategories.length > 0;
	}, [item]);

	const handleAction = async () => {
		if (!item || !selectedSize) return;

		if (!session?.user?.id) {
			toast.error(t("home.item_dialog.sign_in_required"));
			onOpenChange(false);
			return;
		}

		if (!session?.user?.emailVerified) {
			setShowVerifyBanner(true);
			return;
		}

		// If extras are available, open the extras dialog
		if (hasExtras) {
			setIsExtrasOpen(true);
			return;
		}

		// Otherwise add/update directly
		try {
			setIsAdding(true);

			if (cartItem) {
				await updateItem({
					cartItemId: cartItem.id,
					quantity: {
						quantity,
						menuSizeId: parseInt(selectedSize),
					},
				});
				toast.success(t("home.item_dialog.cart_updated"));
			} else {
				await addToCart({
					userId: session.user.id,
					item: {
						menuItemId: item.id,
						menuSizeId: parseInt(selectedSize),
						quantity,
						extras: [],
					},
				});
				splash.show({
					title: t("home.item_dialog.delicious_choice"),
					description: t("home.item_dialog.added_to_cart", { name: item.name }),
					variant: "celebration",
					duration: 3000,
				});
			}

			onOpenChange(false);
		} catch (error) {
			toast.error(
				cartItem
					? t("home.item_dialog.failed_to_update")
					: t("home.item_dialog.failed_to_add"),
			);
			console.error("Error modifying cart:", error);
		} finally {
			setIsAdding(false);
		}
	};

	if (!item) return null;

	return (
		<>
			<Dialog open={open} onOpenChange={onOpenChange}>
				<DialogContent className="sm:max-w-[600px] p-0 max-h-[90dvh] overflow-hidden rounded-2xl border-0 bg-card flex flex-col">
					{/* Hero Image */}
					<div className="relative h-48 sm:h-56 md:h-64 bg-gradient-to-br from-primary/10 to-accent flex-shrink-0">
						{item.imageUrl && (
							<Image
								src={item.imageUrl}
								alt={item.name}
								className="w-full h-full object-cover"
							/>
						)}
						<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

						{/* Close button */}
						<Button
							variant="ghost"
							size="sm"
							className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm border-0 hover:bg-white/30 text-white"
							onClick={() => onOpenChange(false)}
						>
							<Icon name="X" className="w-4 h-4" />
						</Button>
					</div>

					{/* Content */}
					<div className="flex-1 overflow-y-auto p-6 space-y-6">
						{/* Header */}
						<div className="space-y-3">
							<div className="flex items-center gap-2">
								{item.isVegan && (
									<Badge
										variant="secondary"
										className="text-xs bg-green-100 text-green-700 border-green-200"
									>
										🌱 {t("common.vegan")}
									</Badge>
								)}
								{item.isVegetarian && (
									<Badge
										variant="secondary"
										className="text-xs bg-blue-100 text-blue-700 border-blue-200"
									>
										🥬 {t("common.vegetarian")}
									</Badge>
								)}
							</div>

							<div>
								<h2 className="text-2xl font-semibold text-foreground">
									{item.name}
								</h2>
								<div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
									<div className="flex items-center gap-1">
										<Icon name="Star" className="w-4 h-4 text-primary" />
										<span className="text-primary font-medium">
											{item.rating.toFixed(1)}
										</span>
									</div>
									{/* Note: calories and persons removed from database schema */}
									<div className="flex items-center gap-1">
										<Icon name="Users" className="w-4 h-4" />
										{t("home.item_dialog.serves")}
									</div>
								</div>
							</div>

							{item.description && (
								<p className="text-muted-foreground text-sm leading-relaxed">
									{item.description}
								</p>
							)}
						</div>

						{/* Size Selection */}
						<div className="space-y-4">
							<h3 className="text-lg font-medium text-foreground">
								{t("home.item_dialog.choose_size")}
							</h3>
							{itemSizes.length === 0 ? (
								<div className="text-center py-8 text-muted-foreground">
									{t("home.item_dialog.no_sizes")}
								</div>
							) : (
								<div className="grid grid-cols-1 gap-3">
									{itemSizes.map((size) => (
										<div key={size.id} className="relative">
											<input
												type="radio"
												id={size.id.toString()}
												value={size.id.toString()}
												checked={selectedSize === size.id.toString()}
												onChange={() => setSelectedSize(size.id.toString())}
												className="sr-only"
											/>
											<label
												htmlFor={size.id.toString()}
												className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
													selectedSize === size.id.toString()
														? "border-primary bg-primary/5 shadow-sm"
														: "border-border hover:border-primary/50 hover:bg-accent/50"
												}`}
											>
												<div className="flex items-center gap-3">
													<div
														className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
															selectedSize === size.id.toString()
																? "border-primary bg-primary"
																: "border-muted-foreground"
														}`}
													>
														{selectedSize === size.id.toString() && (
															<div className="w-2 h-2 rounded-full bg-white" />
														)}
													</div>
													<span className="font-medium text-foreground">
														{size.sizeName}
													</span>
												</div>
												<span className="text-lg font-semibold text-primary">
													€{parseFloat(size.price).toFixed(2)}
												</span>
											</label>
										</div>
									))}
								</div>
							)}
						</div>

						{/* Quantity & Total */}
						<div className="space-y-4">
							<div className="flex items-center justify-between">
								<h3 className="text-lg font-medium text-foreground">
									{t("common.quantity")}
								</h3>
								<div className="flex items-center gap-2 bg-muted rounded-full p-1">
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setQuantity(Math.max(1, quantity - 1))}
										disabled={quantity <= 1}
										className="w-8 h-8 rounded-full p-0 hover:bg-background"
									>
										<Icon name="Minus" className="w-4 h-4" />
									</Button>
									<span className="w-8 text-center font-medium text-sm">
										{quantity}
									</span>
									<Button
										variant="ghost"
										size="sm"
										onClick={() => setQuantity(quantity + 1)}
										className="w-8 h-8 rounded-full p-0 hover:bg-background"
									>
										<Icon name="Plus" className="w-4 h-4" />
									</Button>
								</div>
							</div>

							<div className="flex items-center justify-between pt-4 border-t">
								<span className="text-lg font-medium text-foreground">
									{t("common.total")}
								</span>
								<span className="text-2xl font-bold text-primary">
									€{totalPrice.toFixed(2)}
								</span>
							</div>
						</div>

						{/* Verification Banner */}
						{showVerifyBanner && (
							<div className="flex items-center gap-3 bg-primary/10 border border-primary/20 rounded-xl p-3">
								<Icon name="Mail" className="w-5 h-5 text-primary shrink-0" />
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium text-foreground">
										{t("auth_guard.verification_required.title")}
									</p>
									<Link
										to="/auth/verify-email"
										className="text-sm text-primary font-medium hover:underline"
									>
										{t("auth_guard.actions.verify_email")} →
									</Link>
								</div>
							</div>
						)}

						{/* Action Button */}
						<Button
							onClick={handleAction}
							disabled={!selectedSize || isAdding}
							className="w-full h-12 text-lg font-medium rounded-xl"
							size="lg"
							variant="primary"
						>
							{isAdding ? (
								<>
									<Icon name="LoaderCircle" className="animate-spin size-8" />
									{t("common.working")}
								</>
							) : hasExtras ? (
								<>
									{t("home.item_dialog.customize")}
									<Icon name="ArrowRight" className="w-5 h-5" />
								</>
							) : cartItem ? (
								<>
									<Icon name="Save" className="size-6" />
									{t("home.item_dialog.update_cart")}
								</>
							) : (
								<>
									<Icon name="Plus" className="size-6" />
									{t("home.item_dialog.add_to_cart")}
								</>
							)}
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			{/* Nested Extras Dialog */}
			{hasExtras && selectedSizeData && (
				<ExtrasDialog
					open={isExtrasOpen}
					onOpenChange={setIsExtrasOpen}
					item={item}
					selectedSize={selectedSize}
					selectedSizeName={selectedSizeData.sizeName}
					quantity={quantity}
					basePrice={parseFloat(selectedSizeData.price)}
					onItemAddedToCart={() => onOpenChange(false)}
					cartItem={cartItem}
				/>
			)}
		</>
	);
}
